import React from 'react'
import { useNavigate } from 'react-router-dom'

import useDidMount from '@divvy-web/hooks.usedidmount'
import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import BasicButton, {
  BASIC_BUTTON_COLOR_ACTION,
  BASIC_BUTTON_SIZE_SMALL,
  BASIC_BUTTON_TYPE_OUTLINED,
} from '@divvy-web/skylab.basicbutton'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useTrackCollaboratorEvent } from '@sputnik/hooks/useApplicationCollaborators'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import useGetIsLoggedInAsAuthSigner from '@sputnik/hooks/useGetIsLoggedInAsAuthSigner'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useResubmitApplication } from './ResubmitApplication.gql'
import { useSubmitReview } from './SubmitReview.gql'

interface FrozenCreditBannerProps {
  salesforceCreditId: string
  authSignerEmail: string
}

// This banner should only show for the authorized signer
const FrozenCreditBanner = ({ salesforceCreditId, authSignerEmail }: FrozenCreditBannerProps) => {
  const showToast = useToast()
  const navigate = useNavigate()

  const [companyDivvyUuid] = useDivvyUUID(salesforceCreditId)
  const { isLoggedInUserAuthorizedSigner } = useGetIsLoggedInAsAuthSigner({
    authSignerEmail,
  })

  const { trackCollaboratorEvent } = useTrackCollaboratorEvent({
    salesforceCreditId,
  })

  useDidMount(() => {
    trackCollaboratorEvent('se_signup__application_dashboard_action_needed_frozen_credit__presented', (role) => ({
      person_type: role,
    }))
  })

  const [resubmitApplication, { loading: isLoadingResubmitApplication }] = useResubmitApplication({
    variables: {
      reason: 'frozen',
      salesforceCreditId,
    },
    onCompleted: () => {
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Application has been successfully been resubmitted.'
          id='sputnik.FrozenCreditBanner__HCbmUR'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      logInfo('resubmitApplication', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: `Success. Sending user to ${PATHNAME_CONSTS.SUBMITTING_APPLICATION} page`,
        },
        eventName: 'Resubmit Application click',
      })

      navigate(`/app/${salesforceCreditId}/${PATHNAME_CONSTS.SUBMITTING_APPLICATION}`)
    },
    onError: (e) => {
      logError(e, {
        attributes: {
          action: 'resubmitApplication',
          creditAppId: salesforceCreditId,
          result: 'Error when resubmitting credit application',
        },
        eventName: 'Resubmit Application click',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='There was an error re-submitting your application, please request review.'
          id='sputnik.FrozenCreditBanner__Vwqqe8'
        />,
        { className: 'fs-unmask' },
      )
    },
    refetchQueries: ['GetCreditApplicationDetails'],
  })

  const [submitReview, { loading: isLoadingSubmitReview }] = useSubmitReview({
    variables: {
      companyDivvyUuid,
      salesforceCreditId,
    },
    onCompleted: () => {
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Application has been successfully sent to review.'
          id='sputnik.FrozenCreditBanner__CNH6Ya'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      logInfo('unfreezeCreditApplicationRequest', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Success',
        },
        eventName: 'Request Review click',
      })
    },
    onError: (e) => {
      logError(e, {
        attributes: {
          action: 'unfreezeCreditApplicationRequest',
          creditAppId: salesforceCreditId,
          result: 'Error when requesting to unfreeze credit application',
        },
        eventName: 'Request Review click',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='There was an error when requesting to unfreeze your application'
          id='sputnik.FrozenCreditBanner__51LlE6'
        />,
        { className: 'fs-unmask' },
      )
    },
    refetchQueries: ['GetCreditApplicationDetails'],
  })

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Error}
        subtext={
          isLoggedInUserAuthorizedSigner ? (
            <FormattedMessage
              defaultMessage='Please contact Experian to unfreeze your credit and then resubmit the application from this dashboard. <button>Resubmit application</button> <buttonSecondary>Request review</buttonSecondary>'
              id='sputnik.FrozenCreditBanner__dezVS5'
              values={{
                button: (chunks) => (
                  <BasicButton
                    color={BASIC_BUTTON_COLOR_ACTION}
                    disabled={isLoadingResubmitApplication}
                    size={BASIC_BUTTON_SIZE_SMALL}
                    type={BASIC_BUTTON_TYPE_OUTLINED}
                    onClick={() => {
                      trackCollaboratorEvent(
                        'se_signup__application_dashboard_action_needed_resubmit_application__fired',
                      )
                      resubmitApplication()
                    }}
                  >
                    {chunks}
                  </BasicButton>
                ),
                buttonSecondary: (chunks) => (
                  <BasicButton
                    color={BASIC_BUTTON_COLOR_ACTION}
                    disabled={isLoadingSubmitReview}
                    size={BASIC_BUTTON_SIZE_SMALL}
                    type={BASIC_BUTTON_TYPE_OUTLINED}
                    onClick={() => {
                      trackCollaboratorEvent('se_signup__application_dashboard_action_needed_request_review__fired')
                      submitReview()
                    }}
                  >
                    {chunks}
                  </BasicButton>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage='They must contact Experian to unfreeze their credit and then resubmit the application from their dashboard.'
              id='sputnik.FrozenCreditBanner__0+95MK'
            />
          )
        }
        title={
          <FormattedMessage
            defaultMessage='{isAuthSigner, select,
            true {Your credit is frozen}
            other {The authorized signer’s credit is frozen}}'
            id='sputnik.FrozenCreditBanner__1JRfgv'
            values={{ isAuthSigner: isLoggedInUserAuthorizedSigner }}
          />
        }
      />
    </div>
  )
}

export default FrozenCreditBanner
